import { useCallback, useEffect, useMemo, useState } from "react";

import AddRounded from "@mui/icons-material/AddRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import EventBusyOutlined from "@mui/icons-material/EventBusyOutlined";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import SearchRounded from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { useEnsureUsers } from "../../hooks/useEnsureReferenceData";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { closingCoverageApi } from "../../api/closingCoverageApi";
import { closingReadinessApi, READINESS_PIPELINES } from "../../api/closingReadinessApi";
import CoverageGridView from "./CoverageGridView";
import CoverageDayView from "./CoverageDayView";
import OOOFormModal from "./OOOFormModal";
import OOODetailDrawer from "./OOODetailDrawer";
import BankHolidayModal from "./BankHolidayModal";
// Loan-closing layer: the schedule cards (GET /closing/schedule,
// closing-readiness.md §2) + the detail drawer shared with the loan-closings
// calendar (kept as a hidden sibling screen).
import ClosingDetailDrawer from "../LoanClosingsCalendarScreen/ClosingDetailDrawer";
import {
  formatCompactDollar,
  loanClosingDate,
  loanId,
  orderCards,
  sumLoanAmounts,
} from "../LoanClosingsCalendarScreen/closingCalendarUtils";
import {
  cardMatchesSearch,
  compareCardsForDay,
  scopeCards,
} from "../../components/closingReadiness/closingReadinessUtils";
import { ReadinessLegendContent } from "../../components/closingReadiness/ReadinessLegend";
import { amber, slate, status } from "../../theme/colors";
import { segmentedGroupSx } from "../../theme/activeStates";
import { partnerStorage } from "../../utils/storage/partnerStorage";
import {
  C,
  bucketCoverage,
  dayKey,
  reasonStyle,
  tzShortLabel,
  ymd,
} from "./coverageUtils";

// Which pipeline's fundings to overlay: closings (preclose), trade settlements
// (acquisitions), or both. Never rendered with one vocabulary.
const PIPELINE_FILTERS = [
  { label: "Closings + settlements", value: READINESS_PIPELINES.ALL },
  { label: "Closings only", value: READINESS_PIPELINES.PRECLOSE },
  { label: "Trade settlements only", value: READINESS_PIPELINES.ACQUISITION },
];

const ALL_STAFF = "all";

// Access-group filter for the team calendar (`group` param). "All staff" omits
// the param. Extend as more closing-coverage groups are defined server-side.
const GROUPS = [
  // A real sentinel, not "" — an empty value renders as a blank select.
  { label: "All staff", value: ALL_STAFF },
  { label: "Closers", value: "Closers" },
  { label: "US Underwriters", value: "US Underwriters" },
];

const VIEWS = ["month", "week", "day"];
const VIEW_LABELS = { month: "Month", week: "Week", day: "Day" };

// The clickable totals. `test` is over a pending (non-closed) card; ready =
// nothing near-term (ready / ready with items), attention = the red + amber
// levels, stale = the ≥ 7 day quiet flag (overlaps the other two).
const STAT_FILTERS = {
  ready: {
    key: "ready",
    label: "ready",
    pillLabel: "Ready",
    test: (c) => c?.readinessLevel === "ready" || c?.readinessLevel === "ready_with_items",
    dot: status.success.solid,
    tint: status.success.bg,
    text: status.success.fg,
  },
  attention: {
    key: "attention",
    label: "need attention",
    pillLabel: "Needs attention",
    test: (c) => ["blocked", "not_scheduled", "at_risk"].includes(c?.readinessLevel),
    dot: amber.main,
    tint: amber.light,
    text: amber.chipText,
  },
  stale: {
    key: "stale",
    label: "stale",
    pillLabel: "Stale · no activity ≥ 7d",
    test: (c) => !!c?.stale,
    dot: status.warning.fg,
    tint: status.warning.bg,
    text: status.warning.fg,
  },
};

// ── Remembered header filters ────────────────────────────────────────────────
// Everything the two header rows set (view, totals filter, toggles, selects,
// search) is kept in partner-scoped localStorage so the calendar reopens the
// way it was left. The period (anchor date) is deliberately not remembered —
// the calendar always opens on today.
const FILTERS_KEY = "closingCalendar.filters";
const FILTER_DEFAULTS = {
  view: "month",
  group: ALL_STAFF,
  layers: { closings: true, coverage: true, holidays: true },
  closingsPipeline: READINESS_PIPELINES.ALL,
  stage: "funding",
  showClosed: false,
  statFilter: null,
  search: "",
};
const PIPELINE_VALUES = PIPELINE_FILTERS.map((p) => p.value);
const GROUP_VALUES = GROUPS.map((g) => g.value);
const loadFilters = () => {
  try {
    const raw = partnerStorage.getItem(FILTERS_KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (!v || typeof v !== "object") return FILTER_DEFAULTS;
    // Validate each field against what the header can actually set, so a
    // stale or hand-edited value can't leave a control in an unknown state.
    return {
      view: VIEWS.includes(v.view) ? v.view : FILTER_DEFAULTS.view,
      group: GROUP_VALUES.includes(v.group) ? v.group : FILTER_DEFAULTS.group,
      layers: { ...FILTER_DEFAULTS.layers, ...(v.layers && typeof v.layers === "object" ? v.layers : {}) },
      closingsPipeline: PIPELINE_VALUES.includes(v.closingsPipeline) ? v.closingsPipeline : FILTER_DEFAULTS.closingsPipeline,
      stage: v.stage === "all" ? "all" : "funding",
      showClosed: v.showClosed === true,
      statFilter: v.statFilter in STAT_FILTERS ? v.statFilter : null,
      search: typeof v.search === "string" ? v.search : "",
    };
  } catch {
    return FILTER_DEFAULTS;
  }
};
const saveFilters = (filters) => {
  try {
    partnerStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch {
    // Storage full / disabled — the calendar still works, it just won't remember.
  }
};

const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

// Outlined pill button (Today / Bank holidays / Legend).
const pillSx = {
  textTransform: "none",
  borderRadius: "99px",
  fontSize: 13.5,
  fontWeight: 500,
  px: 2,
  py: "5px",
  background: C.surface,
  color: C.ink,
  borderColor: C.line,
  "&:hover": { background: C.lineSoft, borderColor: C.line },
};

const NavChevron = ({ onClick, label, children }) => (
  <IconButton
    size="small"
    onClick={onClick}
    aria-label={label}
    sx={{ width: 32, height: 32, color: C.sub, "&:hover": { background: C.lineSoft } }}
  >
    {children}
  </IconButton>
);

// A clickable total. Active = tinted in the stat's own colour with a clear
// glyph; the count stays ink so the number reads the same on and off.
const StatButton = ({ value, label, dot, tint, on, onClick, title }) => (
  <Flx
    ac
    g={1}
    component="button"
    type="button"
    onClick={onClick}
    title={title}
    sx={{
      font: "inherit",
      cursor: "pointer",
      userSelect: "none",
      border: `1px solid ${on ? `${dot}66` : "transparent"}`,
      background: on ? tint : "transparent",
      borderRadius: "10px",
      px: 1.5,
      py: 0.75,
      transition: "all 0.13s",
      "&:hover": { background: on ? tint : C.lineSoft },
      "&:focus-visible": { outline: `2px solid ${C.accent}`, outlineOffset: 1 },
    }}
  >
    {dot && <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />}
    <Txt sx={{ fontSize: 16, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{value}</Txt>
    <Txt sx={{ fontSize: 13, fontWeight: on ? 600 : 500, color: on ? C.ink : C.sub, whiteSpace: "nowrap" }}>{label}</Txt>
    {on && (
      <Flx center sx={{ width: 16, height: 16, borderRadius: "50%", background: "rgba(15,23,42,.12)", color: C.sub }}>
        <CloseRounded sx={{ fontSize: 11 }} />
      </Flx>
    )}
  </Flx>
);

// A toggle chip with a colour dot (the layer toggles + the scope toggles).
const FilterChip = ({ on, color, label, onClick, title }) => (
  <Flx
    ac
    g={0.75}
    onClick={onClick}
    title={title}
    sx={{
      px: 1.6,
      py: "5px",
      borderRadius: "99px",
      cursor: "pointer",
      userSelect: "none",
      fontSize: 12.5,
      fontWeight: on ? 600 : 500,
      border: `1px solid ${on ? `${color}66` : C.line}`,
      background: on ? `${color}14` : C.surface,
      color: on ? C.ink : C.sub,
      whiteSpace: "nowrap",
      transition: "all 0.13s",
      "&:hover": { background: on ? `${color}1f` : C.lineSoft },
    }}
  >
    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: on ? color : C.line, flexShrink: 0 }} />
    {label}
  </Flx>
);

const StatDivider = () => <Box sx={{ width: "1px", height: 22, background: C.line, mx: 0.5, flexShrink: 0 }} />;

// A compact pill select for the strip (pipeline / staff group).
const compactSelectSx = {
  minWidth: 0,
  ".MuiInputBase-root": {
    fontSize: 12.5,
    fontWeight: 500,
    color: C.sub,
    borderRadius: "99px",
    background: C.surface,
    height: 30,
  },
  ".MuiSelect-select": { py: "4px !important", pl: 1.6 },
  ".MuiOutlinedInput-notchedOutline": { borderColor: C.line },
};

// Closing-team coverage calendar (frontend_documentation/closing-coverage-calendar.md):
// the fundings feed (closing-readiness.md §2) overlaid with out-of-office
// coverage and bank-holiday / wire-block days, with the user's timezone
// driving how their own times are interpreted.
const ClosingCalendarScreen = () => {
  useDocumentTitle("Closing Calendar");
  // The OOO form + coverage entries resolve user emails to names via the shared
  // `users` slice — pull it lazily (cache-gated) rather than via a global fetch.
  useEnsureUsers();
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin, isCloser } = usePermissionsHook();

  const currentEmail = useSelector((state) => state?.user?.emailAddress);
  const profileTz = useSelector((state) => state?.user?.timezone);

  // ── Period + view ──────────────────────────────────────────────────────────
  // `anchor` is the day being looked at; the view decides how much of the
  // calendar around it is drawn (its month grid, its week, or just the day).
  // Header filters start from whatever was remembered last time.
  const [initialFilters] = useState(loadFilters);
  const [view, setView] = useState(initialFilters.view);
  const [anchor, setAnchor] = useState(() => new Date());
  const [group, setGroup] = useState(initialFilters.group);
  const [entries, setEntries] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formEntry, setFormEntry] = useState(null);
  const [holidaysOpen, setHolidaysOpen] = useState(false);
  const [legendAnchor, setLegendAnchor] = useState(null);
  const [tzAnchor, setTzAnchor] = useState(null);

  // Loan-closings layer (overlaid on the coverage calendar).
  const [closings, setClosings] = useState([]);
  const [closingsLoading, setClosingsLoading] = useState(true);
  const [selectedClosing, setSelectedClosing] = useState(null);
  const [closingDrawerOpen, setClosingDrawerOpen] = useState(false);

  // Which layers are shown on the grid.
  const [layers, setLayers] = useState(initialFilters.layers);
  const toggleLayer = useCallback(
    (key) => setLayers((p) => ({ ...p, [key]: !p[key] })),
    [],
  );

  const [timezones, setTimezones] = useState([]);
  const [tz, setTz] = useState(() => browserTz());

  const canManageHolidays = isCloser || isAdmin;

  // The fetch window is always the anchor's month grid (leading + trailing
  // week included): it contains every week / day the other views can show, so
  // switching view or stepping a day inside the month never refetches.
  const monthKey = format(anchor, "yyyy-MM");
  const windowRange = useMemo(() => {
    const first = new Date(Number(monthKey.slice(0, 4)), Number(monthKey.slice(5, 7)) - 1, 1);
    const start = startOfWeek(startOfMonth(first));
    const end = endOfWeek(endOfMonth(first));
    return { start: ymd(start), end: ymd(end) };
  }, [monthKey]);

  // The days the current view draws.
  const visibleDays = useMemo(() => {
    if (view === "day") return [anchor];
    if (view === "week") return eachDayOfInterval({ start: startOfWeek(anchor), end: endOfWeek(anchor) });
    return eachDayOfInterval({
      start: startOfWeek(startOfMonth(anchor)),
      end: endOfWeek(endOfMonth(anchor)),
    });
  }, [view, anchor]);
  const visibleKeys = useMemo(() => new Set(visibleDays.map(dayKey)), [visibleDays]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Fetch coverage + holidays for the visible window.
  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      closingCoverageApi.getTeamCalendar({ ...windowRange, group: group === ALL_STAFF ? undefined : group }),
      closingCoverageApi.getBankHolidays(windowRange),
    ])
      .then(([cal, hol]) => {
        if (!active) return;
        setEntries(Array.isArray(cal?.entries) ? cal.entries : []);
        setHolidays(Array.isArray(hol?.holidays) ? hol.holidays : []);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Coverage calendar fetch error:", err);
        setEntries([]);
        setHolidays([]);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [windowRange, group, reloadKey]);

  // Timezone options + seed from the profile.
  useEffect(() => {
    closingCoverageApi
      .getTimezones()
      .then((d) => setTimezones(Array.isArray(d?.timezones) ? d.timezones : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (profileTz) setTz(profileTz);
  }, [profileTz]);

  const onChangeTz = (value) => {
    setTzAnchor(null);
    if (value === tz) return;
    setTz(value);
    closingCoverageApi
      .setMyTimezone(value)
      .then((r) =>
        enqueueSnackbar(`Timezone set to ${tzShortLabel(r?.timezone || value)}.`, {
          variant: "success",
        }),
      )
      .catch((err) => {
        const detail = err?.response?.data?.detail || "Could not set timezone.";
        enqueueSnackbar(String(detail), { variant: "error" });
      });
  };

  // Loan fundings — the schedule cards for the visible window (both pipelines
  // by default), bucketed by funding date and overlaid on the calendar.
  // Refetched on month / pipeline change and after a schedule edit in the
  // drawer (`closingsReloadKey`).
  const [closingsPipeline, setClosingsPipeline] = useState(initialFilters.closingsPipeline);
  // Scope: the feed carries every stage + closed loans; default to what the
  // old calendar showed — funding-stage loans, closed hidden.
  const [stage, setStage] = useState(initialFilters.stage);
  const [showClosed, setShowClosed] = useState(initialFilters.showClosed);
  // The clickable total that is filtering everything (null = none).
  const [statFilter, setStatFilter] = useState(initialFilters.statFilter);
  // Free-text search over the fundings (loan #, EV id, borrower, address…).
  const [search, setSearch] = useState(initialFilters.search);

  // Remember the header filters whenever one changes.
  useEffect(() => {
    saveFilters({ view, group, layers, closingsPipeline, stage, showClosed, statFilter, search });
  }, [view, group, layers, closingsPipeline, stage, showClosed, statFilter, search]);
  const [closingsReloadKey, setClosingsReloadKey] = useState(0);
  const reloadClosings = useCallback(() => setClosingsReloadKey((k) => k + 1), []);
  useEffect(() => {
    let active = true;
    setClosingsLoading(true);
    closingReadinessApi
      .getSchedule({ ...windowRange, pipeline: closingsPipeline })
      .then((data) => {
        if (!active) return;
        setClosings(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Closing schedule fetch error:", err);
        setClosings([]);
      })
      .finally(() => active && setClosingsLoading(false));
    return () => {
      active = false;
    };
  }, [windowRange, closingsPipeline, closingsReloadKey]);

  // Scope (stage + closed toggle + search), then the stat filter. A closed
  // loan never matches an active stat filter — the totals are over pending
  // fundings. Search narrows the scope, so the totals follow it.
  const scopedClosings = useMemo(
    () => scopeCards(closings, { stage, showClosed }).filter((c) => cardMatchesSearch(c, search)),
    [closings, stage, showClosed, search],
  );
  const filteredClosings = useMemo(() => {
    if (!statFilter) return scopedClosings;
    const def = STAT_FILTERS[statFilter];
    return scopedClosings.filter((c) => !c?.closed && def.test(c));
  }, [scopedClosings, statFilter]);

  const closingsByDay = useMemo(() => {
    const map = new Map();
    filteredClosings.forEach((loan) => {
      const d = loanClosingDate(loan);
      if (!d) return;
      const k = dayKey(d);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(loan);
    });
    // Worst readiness first so a short cell surfaces what needs attention.
    map.forEach((list) => list.sort(compareCardsForDay));
    return map;
  }, [filteredClosings]);

  const { byDay, holidayByDay } = useMemo(
    () => bucketCoverage(entries, holidays),
    [entries, holidays],
  );

  // The totals strip counts the pending fundings the current view draws
  // (before the stat filter, so the numbers stay put while filtering).
  const inView = useCallback(
    (card) => {
      const d = loanClosingDate(card);
      return d ? visibleKeys.has(dayKey(d)) : false;
    },
    [visibleKeys],
  );
  const pendingInView = useMemo(
    () => scopedClosings.filter((c) => !c?.closed && inView(c)),
    [scopedClosings, inView],
  );
  const counts = useMemo(
    () => ({
      all: pendingInView.length,
      ready: pendingInView.filter(STAT_FILTERS.ready.test).length,
      attention: pendingInView.filter(STAT_FILTERS.attention.test).length,
      stale: pendingInView.filter(STAT_FILTERS.stale.test).length,
    }),
    [pendingInView],
  );
  // What the active stat filter is showing (the filter bar).
  const matchedInView = useMemo(
    () => (statFilter ? filteredClosings.filter(inView) : []),
    [filteredClosings, inView, statFilter],
  );
  // The drawer pages through the visible cards in calendar order.
  const orderedClosings = useMemo(
    () => orderCards(filteredClosings.filter(inView)),
    [filteredClosings, inView],
  );

  const onSelectEntry = useCallback((entry) => {
    setSelectedEntry(entry);
    setDrawerOpen(true);
  }, []);

  // The loan open in the detail drawer reads as selected in the calendar.
  const selectedClosingId = closingDrawerOpen && selectedClosing ? loanId(selectedClosing) : null;

  const onSelectClosing = useCallback((loan) => {
    setSelectedClosing(loan);
    setClosingDrawerOpen(true);
  }, []);

  const openCreate = useCallback(() => {
    setFormEntry(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((entry) => {
    setDrawerOpen(false);
    setFormEntry(entry);
    setFormOpen(true);
  }, []);

  // ‹ › step one period of the current view.
  const shift = useCallback(
    (n) =>
      setAnchor((d) =>
        view === "day" ? addDays(d, n) : view === "week" ? addWeeks(d, n) : addMonths(d, n),
      ),
    [view],
  );
  const goToday = useCallback(() => setAnchor(new Date()), []);
  const openDay = useCallback((date) => {
    setAnchor(date);
    setView("day");
  }, []);
  const toggleStat = useCallback((key) => setStatFilter((cur) => (cur === key ? null : key)), []);

  const title = useMemo(() => {
    if (view === "day") return format(anchor, "EEEE, MMMM d, yyyy");
    if (view === "week") {
      const a = startOfWeek(anchor);
      const b = endOfWeek(anchor);
      return isSameMonth(a, b)
        ? `${format(a, "MMM d")} – ${format(b, "d, yyyy")}`
        : `${format(a, "MMM d")} – ${format(b, "MMM d, yyyy")}`;
    }
    return format(anchor, "MMMM yyyy");
  }, [view, anchor]);

  const activeStat = statFilter ? STAT_FILTERS[statFilter] : null;
  const anchorKey = dayKey(anchor);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        background: C.surface,
      }}
    >
      {/* Row 1 — period + view */}
      <Flx ac wrap g={1.75} sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
        <Flx ac g={1.25}>
          <Flx center sx={{ width: 34, height: 34, borderRadius: "9px", background: C.accent, color: "#fff" }}>
            <EventBusyOutlined sx={{ fontSize: 18 }} />
          </Flx>
          <Txt sx={{ fontSize: 17, fontWeight: 600, color: C.ink, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
            Closing Coverage
          </Txt>
        </Flx>
        <Button variant="outlined" onClick={goToday} sx={pillSx}>
          Today
        </Button>
        <Flx ac>
          <NavChevron onClick={() => shift(-1)} label="Previous">
            <ChevronLeftRounded sx={{ fontSize: 20 }} />
          </NavChevron>
          <NavChevron onClick={() => shift(1)} label="Next">
            <ChevronRightRounded sx={{ fontSize: 20 }} />
          </NavChevron>
        </Flx>
        <Txt sx={{ fontSize: 19, fontWeight: 500, color: C.ink, minWidth: 190, whiteSpace: "nowrap" }}>{title}</Txt>
        {(loading || closingsLoading) && <CircularProgress size={14} sx={{ color: C.faint }} />}

        <Box sx={{ flex: 1 }} />

        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_, v) => v && setView(v)}
          aria-label="View"
          sx={segmentedGroupSx({ fontSize: 13 })}
        >
          {VIEWS.map((v) => (
            <ToggleButton key={v} value={v}>
              {VIEW_LABELS[v]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          onClick={openCreate}
          sx={{ textTransform: "none", borderRadius: "99px", fontWeight: 600, fontSize: 13.5, px: 2, py: "5px", boxShadow: "none" }}
        >
          Out of office
        </Button>
        <Button variant="outlined" onClick={() => setHolidaysOpen(true)} sx={pillSx}>
          Bank holidays
        </Button>
      </Flx>

      {/* Row 2 — clickable totals · layer / scope toggles · legend */}
      <Flx ac wrap g={1} sx={{ px: 2.5, py: 1, borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
        <StatButton
          value={counts.all}
          label="fundings"
          on={false}
          onClick={() => setStatFilter(null)}
          title="Pending fundings in view · click to clear the filter"
        />
        <StatDivider />
        {Object.values(STAT_FILTERS).map((f) => (
          <StatButton
            key={f.key}
            value={counts[f.key]}
            label={f.label}
            dot={f.dot}
            tint={f.tint}
            on={statFilter === f.key}
            onClick={() => toggleStat(f.key)}
            title={statFilter === f.key ? "Clear filter" : `Show only ${f.label}`}
          />
        ))}
        <StatDivider />
        <FilterChip
          on={showClosed}
          color={slate[400]}
          label="Show closed"
          onClick={() => setShowClosed((v) => !v)}
          title="Closed / settled loans are in the feed but hidden by default"
        />
        <FilterChip
          on={stage === "all"}
          color={slate[500]}
          label="All stages"
          onClick={() => setStage((s) => (s === "all" ? "funding" : "all"))}
          title="Off: Clear To Close / funding-stage loans only"
        />
        <FilterChip on={layers.coverage} color={reasonStyle("personal").c} label="Out of office" onClick={() => toggleLayer("coverage")} />
        <FilterChip on={layers.holidays} color={C.block} label="Holidays" onClick={() => toggleLayer("holidays")} />

        <Box sx={{ flex: 1 }} />

        {/* A lone quick-filter — the one control that may keep its own
            placeholder rather than a label above. */}
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search loan #, EV id, borrower, address…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 17, color: C.faint }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search" sx={{ p: 0.25, color: C.faint }}>
                  <CloseRounded sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            ...compactSelectSx,
            width: { xs: 200, md: 260 },
            ".MuiInputBase-root": { ...compactSelectSx[".MuiInputBase-root"], pl: 1.25 },
            ".MuiInputBase-input": { py: "4px", pl: 0, fontSize: 12.5 },
          }}
        />

        <TextField
          select
          size="small"
          value={closingsPipeline}
          onChange={(e) => setClosingsPipeline(e.target.value)}
          sx={compactSelectSx}
        >
          {PIPELINE_FILTERS.map((p) => (
            <MenuItem key={p.value} value={p.value} sx={{ fontSize: 13 }}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          sx={compactSelectSx}
        >
          {GROUPS.map((g) => (
            <MenuItem key={g.label} value={g.value} sx={{ fontSize: 13 }}>
              {g.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Timezone — how this user's out-of-office times are read. */}
        <Tooltip title={`Your timezone · ${tzShortLabel(tz) || "not set"}`} arrow>
          <IconButton
            size="small"
            onClick={(e) => setTzAnchor(e.currentTarget)}
            sx={{ width: 30, height: 30, border: `1px solid ${C.line}`, color: C.sub, background: C.surface }}
          >
            <PublicOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={tzAnchor} open={Boolean(tzAnchor)} onClose={() => setTzAnchor(null)}>
          {(timezones.length ? timezones : tz ? [tz] : []).map((z) => (
            <MenuItem key={z} selected={z === tz} onClick={() => onChangeTz(z)} sx={{ fontSize: 13, minWidth: 180 }}>
              <ListItemIcon sx={{ minWidth: 26 }}>
                {z === tz && <CheckRounded sx={{ fontSize: 16, color: C.accent }} />}
              </ListItemIcon>
              {tzShortLabel(z)}
            </MenuItem>
          ))}
        </Menu>

        <Button
          variant="outlined"
          startIcon={<HelpOutlineRounded sx={{ fontSize: "16px !important" }} />}
          onClick={(e) => setLegendAnchor(e.currentTarget)}
          sx={pillSx}
        >
          Legend
        </Button>
        <Popover
          open={Boolean(legendAnchor)}
          anchorEl={legendAnchor}
          onClose={() => setLegendAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { mt: 1, width: 680, maxWidth: "calc(100vw - 32px)", maxHeight: "calc(100vh - 140px)", overflowY: "auto", borderRadius: "14px", border: `1px solid ${C.line}`, boxShadow: "0 12px 44px -8px rgba(15,23,42,.25)", p: 2.25 } } }}
        >
          <ReadinessLegendContent
            stacked
            dev={isAdmin}
            extraGroups={[
              {
                title: "Coverage · out of office & holidays",
                items: [
                  ...["PTO", "Sick", "Appointment"].map((r) => {
                    const st = reasonStyle(r);
                    return {
                      key: r,
                      swatch: null,
                      frameSx: { background: st.bg, border: `1.5px solid ${st.c}` },
                      label: r,
                      hint: "a teammate out of office",
                      logic: {
                        when: `An out-of-office entry whose reason is "${r}".`,
                        rules: [
                          "one chip per day from start to end, read in the entry's own timezone",
                          "PTO / Sick / Appointment have fixed colours; any other reason gets a stable hashed colour",
                        ],
                        source: "GET /closing/ooo/calendar · entry.reason",
                      },
                    };
                  }),
                  {
                    key: "wire",
                    swatch: null,
                    frameSx: { background: C.blockBg, border: `1.5px solid ${C.block}` },
                    label: "Wires blocked",
                    hint: "a bank holiday, no wires that day",
                    logic: {
                      when: "A bank holiday flagged as a wire-block day.",
                      rules: [
                        "the day cell is tinted and badged",
                        "a funding scheduled on that day is flagged wireBlocked, which makes the loan Blocked",
                      ],
                      source: "GET /closing/bank-holidays · holiday.wireBlocked",
                    },
                  },
                ],
              },
            ]}
          />
        </Popover>
      </Flx>

      {/* Filter result banner */}
      {activeStat && (
        <Flx ac g={1.25} sx={{ px: 2.5, py: "7px", fontSize: 12.5, color: C.sub, background: "#fbfcfd", borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
          <Flx ac g={0.75} sx={{ px: 1.25, py: "3px", borderRadius: "99px", background: activeStat.tint, color: activeStat.text, fontSize: 11.5, fontWeight: 600 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: activeStat.dot }} />
            {activeStat.pillLabel}
          </Flx>
          <Txt sx={{ fontSize: 12.5, color: C.sub }}>
            Showing <Box component="b" sx={{ color: C.ink }}>{matchedInView.length}</Box> loans
            {matchedInView.length ? (
              <>
                {" · "}
                <Box component="b" sx={{ color: C.ink }}>{formatCompactDollar(sumLoanAmounts(matchedInView))}</Box>
              </>
            ) : null}
          </Txt>
          <Button variant="text" size="small" onClick={() => setStatFilter(null)} sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, py: 0, minWidth: 0 }}>
            Clear filter
          </Button>
        </Flx>
      )}

      {/* Calendar */}
      <Box sx={{ position: "relative", flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>
        {view === "day" ? (
          <CoverageDayView
            date={anchor}
            closings={closingsByDay.get(anchorKey) || []}
            entries={byDay.get(anchorKey) || []}
            holiday={holidayByDay.get(anchorKey) || null}
            layers={layers}
            filterLabel={activeStat?.label}
            onSelectClosing={onSelectClosing}
            selectedId={selectedClosingId}
            onSelectEntry={onSelectEntry}
            onSelectHoliday={canManageHolidays ? () => setHolidaysOpen(true) : undefined}
          />
        ) : (
          <CoverageGridView
            view={view}
            days={visibleDays}
            anchorDate={anchor}
            closingsByDay={closingsByDay}
            byDay={byDay}
            holidayByDay={holidayByDay}
            layers={layers}
            onSelectClosing={onSelectClosing}
            selectedId={selectedClosingId}
            onSelectEntry={onSelectEntry}
            onSelectHoliday={canManageHolidays ? () => setHolidaysOpen(true) : undefined}
            onOpenDay={openDay}
          />
        )}
        {closingsLoading && (
          <Flx
            center
            column
            g={1.5}
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(1px)",
              zIndex: 2,
            }}
          >
            <CircularProgress size={26} sx={{ color: C.accent }} />
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: C.sub }}>
              Loading closings…
            </Txt>
          </Flx>
        )}
      </Box>

      <OOODetailDrawer
        entry={selectedEntry}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        currentEmail={currentEmail}
        isAdmin={isAdmin}
        onEdit={openEdit}
        onChanged={reload}
      />
      <ClosingDetailDrawer
        loan={selectedClosing}
        cards={orderedClosings}
        onSelect={setSelectedClosing}
        open={closingDrawerOpen}
        onClose={() => setClosingDrawerOpen(false)}
        onChanged={reloadClosings}
      />
      <OOOFormModal
        show={formOpen}
        entry={formEntry}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
      />
      <BankHolidayModal
        show={holidaysOpen}
        onClose={() => setHolidaysOpen(false)}
        year={anchor.getFullYear()}
        canManage={canManageHolidays}
        onChanged={reload}
      />
    </Box>
  );
};

export default ClosingCalendarScreen;
